/* eslint-disable eqeqeq */
import React, { useEffect, useState } from "react";
import MeetingSdkView from "../../../components/mediaplayer/meeting-sdk";
import SlidesDisplay from "../../../components/slider/producer-slider/producer-slider-view";
import "./producer-dashboard.css";
import PresenterParticipantsView from "../../../components/qna/participants";
import PresenterChatView from "../../../components/qna/chat-view";
import PptViewer from "../../../components/slider/producer-slider/producer-ppt-view";
import { EventFetchSlides } from "../../../services/event/attendee-event-slides";
import { useParams } from "react-router-dom";
import Loader from "../../../utils/loader/loader";
import LivePopup from "../../../components/pop-up/live-pop-up";
import { fetchPollAllResults } from "../../../services/event/pollAllResults";
import { generateClient } from "aws-amplify/api";
import * as config from "../../../aws-exports";
import { toggleSlideControl } from "../../../graphql/mutations";
import {
  onDragSlideForSequence,
  onMeetingStatusChange,
  onSlideControlToggle,
} from "../../../graphql/subscriptions";
import { formatInTimeZone } from "date-fns-tz";
import moment from "moment-timezone";
import { Steps } from "intro.js-react";
import { SlideProvider } from "../../../components/slider/producer-slider/SlideProvider";
import { format } from "date-fns";
import CountdownTimer from "../../../components/countdown/countdowntimer";
import Notes from "../../../components/notes/notes";
import Cookies from "js-cookie";
import SpeakerDropdown from "../../../components/dropdown/SpeakerDropdown";
import { toast } from "react-toastify";
import LiveTimer from "../../../components/timer/LiveTimer";
import {
  initializeDatadogRum,
  getOrCreateSessionId,
  parseUserName,
} from "../../../utils/datadogRum";

const client = generateClient(config);

interface SdkData {
  meetingNumber: string;
  password: string;
  userName: string;
  userEmail: string;
  signature: string;
  tk: string;
  role: number;
  firstName?: string;
  lastName?: string;
}

interface Question {
  pollId: string;
  questionTitle: string;
  options: Record<string, string>;
  sequence: number;
}

interface PollResultData {
  questionTitle: string;
  totalVotes: number;
  results: PollResult[];
}

interface PollResult {
  option: string;
  percentage: number;
  voteCount: number;
}
interface customSlide{
  collateralTypeId:string;
  title:string;
  description:string;
  hyperlinks: any;
}

const ProducerDashboardView: React.FC<{ sdkData: SdkData; eventData: any }> = ({
  sdkData,
  eventData,
}) => {
  const { eventId } = useParams<{ eventId: string }>();

  if (!eventId) {
    throw new Error("Event ID is required but was not provided.");
  }

  const [selectedSlideSequence, setSelectedSlideSequence] = useState<number>(0);
  const [selectedSlideId, setSelectedSlideId] = useState<string>("");
  const [slides, setSlides] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [pollQuestions, setPollQuestions] = useState<Question[]>([]);
  const [allPollResults, setAllPollResults] = useState<PollResultData[]>([]);
  const [slideControlEnabled, setSlideControlEnabled] = useState<boolean>(true);
  const [sliderTop, setSliderTop] = useState(0);
  const [meetingStatus, setMeetingStatus] = useState<boolean>(false);
  const [isSlides, setIsSlides] = useState<boolean>();
  const webinarStartTime = new Date(eventData.date); // Replace with your actual start time
  const webinarDuration = eventData.duration * 60 * 1000; // 45 minutes (or any other duration)
  const eventDate = moment(eventData.date).format("ddd, MMM Do YYYY");
  const eventTime = moment(eventData.date).format("h:mm A z");
  const eventShotTimeZone = moment(eventData.date)
    .tz(eventData.timeZone)
    .format("z");
  const [vBg, setVBg] = useState<any[]>([]);
  const [stepsEnabled, setStepsEnabled] = useState(false);
  const [speakerTitle, setSpeakerTitle] = useState(27);
  const [meetingHeight, setMeetingHeight] = useState(0);
  const [meetingWidth, setMeetingWidth] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const [slidesLength, setSlidesLength] = useState(0);
  const [customSlidesArr, setCustomSlidesArr] = useState<customSlide[]>([]);
  const [isLoadingUpdateSlides, setIsLoadingUpdateSlides] = useState(false);
  const [speakerBio, setSpeakerBio] = useState<any[]>([]);

  // Initialize Datadog RUM for Host and Speaker only
  useEffect(() => {
    const isHostOrSpeaker = sdkData.role === 0 || sdkData.role === 1;
    
    if (!isHostOrSpeaker) {
      return;
    }

    // Get or create a unique session ID for this user session
    const sessionId = getOrCreateSessionId(eventId, sdkData.userEmail);
    
    // Parse user name (prefer provided firstName/lastName, fallback to parsing userName)
    const { firstName, lastName } = parseUserName(
      sdkData.userName,
      sdkData.firstName,
      sdkData.lastName
    );
    
    initializeDatadogRum({
      firstName,
      lastName,
      userEmail: sdkData.userEmail,
      role: sdkData.role,
      eventId: eventId,
      sessionId,
    });
  }, [sdkData.role, sdkData.userName, sdkData.userEmail, sdkData.firstName, sdkData.lastName, eventId]);

  useEffect(() => {
    const words = eventData?.title.split(" ");
    if (words.length > 10) {
      const excessWords = words.length - 10;
      setSpeakerTitle(Math.max(12, 19 - excessWords)); // Decrease font size, but keep a minimum size
    } else {
      setSpeakerTitle(16); // Default font size
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventData.title]);

  const handleGoLiveClick = () => {
    setIsPopupVisible(true);
  };

  const handleClosePopup = () => {
    setIsPopupVisible(false);
  };

  const fetchData = async () => {
    try {
      setIsLoadingUpdateSlides(true);
      const response = await EventFetchSlides(Number(eventId));
      setSlides(response?.collaterals?.slides);
      setSlidesLength(response?.collaterals?.slides.length);
      setPollQuestions(response.collaterals.pollQuestions);
      setMeetingStatus(response.isMeetingLive);
      setCustomSlidesArr(response.collaterals.customSlidesArr);
      setVBg(response?.virtualBackgrounds || []); // Ensure fallback if no virtual backgrounds
      if (response?.currentSlide) {
        setSelectedSlideSequence(response?.currentSlide?.sequence);
        setSelectedSlideId(response?.currentSlide?.collateralTypeId ?response?.currentSlide?.collateralTypeId:response?.currentSlide?.id);
      }
      if (response?.collaterals?.slides.length === 0) {
        setIsSlides(false);
      } else {
        setIsSlides(true);
      }
      const speakers = response?.collaterals?.speakerbio;
      if (sdkData.role === 1) {
        setSlideControlEnabled(true);
      } else {
        const slideControl = speakers.find((u: { email: any }) => u.email === sdkData.userEmail)?.slideControl ?? false;
        setSlideControlEnabled(slideControl);
      }
      if (response?.collaterals?.speakerbio) {
        setSpeakerBio(response.collaterals.speakerbio);
      }
    } catch (error) {
      console.error("Error fetching slides:", error);
    } finally {
      setIsLoadingUpdateSlides(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [eventId]);

  useEffect(() => {
    const fetchPollResult = async () => {
      try {
        const response = await fetchPollAllResults(Number(eventId));
        setAllPollResults(response); // Ensure this response matches the PollResultData[] type
      } catch (error) {
        console.error("Error fetching poll results:", error);
      }
    };

    fetchPollResult();
  }, [eventId]);

  // const slideControlClick = async () => {
  //   try {
  //     const newStatus = !slideControlEnabled;
  //     await client.graphql({
  //       query: toggleSlideControl,
  //       variables: { eventid: eventId, enableSlideControl: newStatus }, // Pass the dynamic value here
  //     });
  //     setSlideControlEnabled(newStatus); // Update the state to reflect the new status
  //   } catch (error) {
  //     console.error("Error toggling slide control:", error);
  //   }
  // };

  const setSliderTopFn = (heightRef: number) => {
    const heightToSlider = heightRef - 261;
    setSliderTop(heightToSlider);
  };

  useEffect(() => {
    // Subscribe to slide control toggle
    const subscription = client.graphql({ query: onSlideControlToggle });
    const observer = subscription.subscribe({
      next: ({ data }) => {
        const controlData = data.onSlideControlToggle;
        if (controlData.eventid === eventId && controlData.email == sdkData.userEmail) {
          setSlideControlEnabled(controlData.enableSlideControl);
          if(sdkData.role == 0){
            toast.success(
              `slide control ${
                controlData.enableSlideControl ? "enabled" : "disabled"
              }!`
            );
          }
        }
      },
      error: (error) => console.warn("Subscription error:", error),
    });

    return () => observer.unsubscribe();
  }, [eventId]);

  useEffect(() => {
    const subscription = client.graphql({ query: onDragSlideForSequence });
    const observer = subscription.subscribe({
      next: ({ data }) => {
        const controlData = data.onDragSlideForSequence;
        if (controlData.eventid === eventId) {
          fetchData();
        }
      },
      error: (error) => console.warn("Subscription error:", error),
    });

    return () => observer.unsubscribe();
  }, [eventId]);

  useEffect(() => {
    const subscription = client.graphql({ query: onMeetingStatusChange });
    const observer = subscription.subscribe({
      next: ({ data }) => {
        const newSlide = data.onMeetingStatusChange;
        if (newSlide && newSlide.eventid == eventId) {
          setMeetingStatus(newSlide.meetingstatusflag);
        }
      },
      error: (error) => console.warn("Subscription error:", error),
    });

    return () => observer.unsubscribe();
  }, [eventId]);

  // Trigger the steps when loading is done
  useEffect(() => {
    if (!isLoading) {
      setStepsEnabled(true);
    }
  }, [isLoading]);

  const allSteps = [
    {
      element: 'button[title="Audio"]',
      intro:
        "Click this icon to turn on the session's audio and choose your speaker output.",
    },
  ];

  const setMeetingHeightFn = (heightRef: number) => {
    const heightToSlider = heightRef;
    setMeetingHeight(heightToSlider);
  };
  const setMeetingWidthFn = (width: number) => {
    setMeetingWidth(width);
  };
  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };
    window.addEventListener("resize", handleResize);
    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const cookieName = `${eventId}-${sdkData.userEmail}`;
    // const cookieName = `${eventId}`;
    const savedSlideControlStatus = Cookies.get(cookieName);
    if (savedSlideControlStatus !== undefined) {
      // If cookie exists, set the state to the saved value (true or false)
      setSlideControlEnabled(savedSlideControlStatus === "true");
    }
  }, [eventId]);

  return (
    <div id="host-dash" className="main-panel-wrapper">
      {isLoading && <Loader />}
      <Steps
        enabled={stepsEnabled}
        steps={allSteps}
        initialStep={0}
        onExit={() => setStepsEnabled(false)}
        options={{
          showButtons: false, // Disables navigation buttons in the footer
          exitOnOverlayClick: true,
          exitOnEsc: true,
        }}
      />
      <div className="presenter-header">
        <div className="left-data">
          {/* <img src={logo} className="logo-wrapper" alt="Logo" /> */}
          <div
            className="event-title"
            style={{
              fontSize: `${speakerTitle}px`,
              transition: "font-size 0.3s ease",
            }}
          >
            {eventData?.title}
          </div>
        </div>
        <div className="right-data">
        {!meetingStatus && (
          <div className="event-timedata">
            <p>
              Start Time: {eventDate} | {eventTime} {eventShotTimeZone}
            </p>
              <CountdownTimer
                webinarStartTime={webinarStartTime}
                webinarDuration={webinarDuration}
                timeZone={eventData.timeZone}
                onStart={() => console.log("Webinar Started")}
                onEnd={() => console.log("Webinar Ended")}
              />
          </div>
        )}

         <LiveTimer isLive={meetingStatus} eventId={Number(eventId)} />
         
          {sdkData.role === 1 && (
            <>
              {/* <button onClick={slideControlClick} className="slide-control-btn">
                {slideControlEnabled
                  ? "Disable Speaker Slide Control"
                  : "Enable Speaker Slide Control"}
              </button> */}
              <SpeakerDropdown
                speakerBio={speakerBio}
                meetingNumber={sdkData.meetingNumber}
              />

              <button onClick={handleGoLiveClick} className={`live-btn ${meetingStatus ? 'end-webinar' : ''}`}>

                {meetingStatus ? "End webinar" : "Go Live"}
              </button>
            </>
          )}
          {sdkData.role === 0 && (
            <>
              {/* <div className="slide-control-status-msg">
                {slideControlEnabled
                  ? "Slide Control is currently enabled."
                  : "Slide Control is currently disabled."}
              </div> */}
              {meetingStatus && (
                <button className="live-indicator">
                  Live
                </button>
              )}
            </>
          )}
          {isPopupVisible && (
            <LivePopup
              sdkData={sdkData}
              setIsPopupVisible={setIsPopupVisible}
              onClose={handleClosePopup}
              mStatus={meetingStatus}
            />
          )}
        </div>
      </div>
      <div className="inner-main-wrapper">
        <div className="left-side-wrapper">
          <MeetingSdkView
            meetingNumber={sdkData.meetingNumber}
            password={sdkData.password}
            userName={sdkData.userName}
            userEmail={sdkData.userEmail}
            leaveUrl="https://example.com"
            signature={sdkData.signature}
            tk={sdkData.tk}
            setLoading={setIsLoading}
            getVideoHeight={setSliderTopFn}
            isSlides={isSlides}
            virtualBg={vBg}
            getHeightSdkMeeting={setMeetingHeightFn}
            getWidthOfMeeting={setMeetingWidthFn}
          />
        </div>
        <div className="live-view-wrapper"  style={{ height: meetingHeight, marginLeft: meetingWidth + 20}}>
          <SlideProvider
            eventId={eventId}
            role={sdkData.role}
            slidesData={slides}
            selectedSlideSequence={selectedSlideSequence}
            userEmail={sdkData.userEmail}
            slideControlEnabled={slideControlEnabled}
          >
            <PptViewer
              slidesData={slides}
              pollData={pollQuestions}
              allPollResultData={allPollResults}
              slideControlEnabled={slideControlEnabled}
              selectedSlideSequence={selectedSlideSequence}
              sdkData={sdkData}
              customSlidesData={customSlidesArr}
              selectedSlideId={selectedSlideId}
            />
          </SlideProvider>
        </div>
      </div>
      <div className="inner-main-wrapper-2">
      <div className="inner-bottom-left-wrapper inner-bottom-slide" style={{marginBottom: '10px'}}>
      <div className="left-side-wrapper left-side-control-wrapper">
            <div className="participants-left-column">
              <PresenterParticipantsView />
            </div>
          </div>
          <div className="slide-right-column">
            {isSlides && (
              <div className="slider-display">
                <SlideProvider
                  eventId={eventId}
                  role={sdkData.role}
                  slidesData={slides}
                  selectedSlideSequence={selectedSlideSequence}
                  userEmail={sdkData.userEmail}
                  setSelectedSlideId={setSelectedSlideId}
                  slideControlEnabled={slideControlEnabled}
                >
                  <SlidesDisplay
                    onSlideSelect={setSelectedSlideSequence}
                    selectedSlideSequence={selectedSlideSequence}
                    slidesData={slides}
                    pollData={pollQuestions}
                    allPollResultData={allPollResults}
                    role={sdkData.role}
                    viewportHeight={viewportHeight}
                    meetingHeight={meetingHeight}
                    slidesLength={slidesLength}
                    customSlidesData={customSlidesArr}
                    userEmail={sdkData.userEmail}
                    isLoadingUpdateSlides={isLoadingUpdateSlides}
                    setIsLoadingUpdateSlides={setIsLoadingUpdateSlides}
                    updateSlides={fetchData}
                    selectedSlideId={selectedSlideId}
                    setSelectedSlideId={setSelectedSlideId}
                    meetingStatus={meetingStatus}
                    slideControlEnabled={slideControlEnabled}
                  />
                </SlideProvider>
              </div>
            )}
          </div>
        </div>
        <div className="inner-bottom-right-wrapper" style={{marginBottom: '10px'}}>
          <div className="right-side-notes-wrapper">
          <Notes  slidesData={slides} selectedSlideSequence={selectedSlideSequence} eventId={eventId}/>
          <div className="right-column">
              <PresenterChatView />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProducerDashboardView;
